/**
 * Scheduling's facility operations table (workforce operational cutover,
 * Monday 2026-09-21).
 *
 *   Facility · Last visit · Next visit · Assigned provider · Manifest ·
 *   Scheduling state · Credentialing · Blocker · Open
 *
 * with the commands Schedule · Reschedule · Assign provider · Confirm ·
 * Complete · Cancel · Record / clear blocker. This replaces the daily
 * "Scheduling Board" use of Monday. Every dated cell says where its value
 * came from — the board's mirror (twice daily) or eMerge's own record — and a
 * native plan is shown ahead of the mirror. Credentialing is shown
 * prominently and enforces nothing (CRED-RULING-001). "Completed" is
 * Scheduling's record that the visit happened as planned; it is not the
 * clinical visit fact and opens no downstream work, and the panel says so.
 *
 * Nothing here names a resident. Buildings, providers and dates only.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import FilledStatusChip from '../../components/status/FilledStatusChip';
import { SCHEDULING_STATUS, SNF_CREDENTIALING } from '../../theme/mondayPalette';
import {
  schedulingApi, outcomeOf, shortDate, freshKey, SchedulingScope, FacilityRow, FacilityTable, FacilityDetail, FacilityLens, FACILITY_LENSES, FACILITY_LENS_LABEL,
  ProviderOption, PlanCommand, PLAN_STATE_LABEL, VisitFact, NextPlan, FacilityProvider, CoverageType, COVERAGE_TYPES,
} from '../finance/financeApi';

/**
 * Who covers the building (readiness audit P1 #8): the coverage record the
 * admin board decides, now decidable by the Scheduling role. A proposal is
 * confirmed or rejected (with a reason); a live assignment is ended (with a
 * reason); a provider can be assigned directly. Nothing here is a visit —
 * the visit's provider lives on the plan above.
 */
function CoverageSection({ facilityId, facilityName, providers, options, canPlan, onChanged }: { facilityId: string; facilityName: string; providers: FacilityProvider[]; options: ProviderOption[]; canPlan: boolean; onChanged: (msg: string) => Promise<void> }) {
  const [act, setAct] = useState<{ kind: 'reject' | 'end' | 'assign'; id?: string } | null>(null);
  const [reason, setReason] = useState('');
  const [provider, setProvider] = useState('');
  const [type, setType] = useState<CoverageType>('PRIMARY');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const run = async (fn: () => Promise<{ success: boolean; code?: string; error?: string; message?: string; status?: number }>, done: string) => {
    setBusy(true); setErr(null);
    try {
      const r = await fn();
      if (!r.success) { setErr(outcomeOf(r, { wrote: true })); return; }
      setAct(null); setReason(''); setProvider('');
      await onChanged(`${facilityName}: ${done}.`);
    } catch { setErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
  };
  return (
    <>
      <h3>Coverage</h3>
      {providers.length === 0 ? <p className="um-quiet">No provider assigned to this building.</p> : (
        <ul aria-label="Coverage">
          {providers.map((p) => (
            <li key={p.assignment_id}>
              {p.name || 'Unnamed'} · {p.coverage_type.toLowerCase()} · {p.coverage}
              {canPlan && p.status === 'PROPOSED' && (
                <span className="sc-cmds" style={{ display: 'inline-flex', marginLeft: 8 }}>
                  <button type="button" className="um-open" disabled={busy} onClick={() => run(() => schedulingApi.decideCoverage(p.assignment_id, 'CONFIRM'), `${p.name || 'provider'} confirmed as ${p.coverage_type.toLowerCase()}`)} aria-label={`Confirm ${p.name || 'provider'} for ${facilityName}`}>Confirm</button>
                  <button type="button" className="um-open" disabled={busy} aria-pressed={act?.kind === 'reject' && act.id === p.assignment_id} onClick={() => { setAct({ kind: 'reject', id: p.assignment_id }); setReason(''); setErr(null); }} aria-label={`Reject ${p.name || 'provider'} for ${facilityName}`}>Reject</button>
                </span>
              )}
              {canPlan && p.status === 'ACTIVE' && (
                <button type="button" className="um-open" style={{ marginLeft: 8 }} disabled={busy} aria-pressed={act?.kind === 'end' && act.id === p.assignment_id} onClick={() => { setAct({ kind: 'end', id: p.assignment_id }); setReason(''); setErr(null); }} aria-label={`End ${p.name || 'provider'} for ${facilityName}`}>End</button>
              )}
            </li>
          ))}
        </ul>
      )}
      {canPlan && !act && <div className="sc-cmds"><button type="button" className="um-open" onClick={() => { setAct({ kind: 'assign' }); setErr(null); }}>Assign coverage</button></div>}
      {canPlan && act && (act.kind === 'reject' || act.kind === 'end') && (
        <form className="um-form" aria-label={`${act.kind === 'reject' ? 'Reject coverage' : 'End coverage'} for ${facilityName}`} onSubmit={(e) => { e.preventDefault(); if (act.kind === 'reject') run(() => schedulingApi.decideCoverage(act.id!, 'REJECT', reason.trim()), 'proposal rejected'); else run(() => schedulingApi.endCoverage(act.id!, reason.trim()), 'coverage ended'); }}>
          <label><span>Why (required)</span><input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} aria-label="Coverage reason" /></label>
          <div className="btns">
            <button type="submit" className="um-open um-primary" disabled={busy || !reason.trim()}>{busy ? 'Recording…' : act.kind === 'reject' ? 'Confirm: Reject' : 'Confirm: End coverage'}</button>
            <button type="button" className="um-open" onClick={() => setAct(null)} disabled={busy}>Back</button>
          </div>
        </form>
      )}
      {canPlan && act && act.kind === 'assign' && (
        <form className="um-form" aria-label={`Assign coverage for ${facilityName}`} onSubmit={(e) => { e.preventDefault(); run(() => schedulingApi.assignCoverage({ facility_id: facilityId, provider_employee_id: provider, coverage_type: type }), 'coverage assigned'); }}>
          <ProviderSelect providers={options} value={provider} onChange={setProvider} label="Provider" />
          <label><span>Coverage type</span><select value={type} onChange={(e) => setType(e.target.value as CoverageType)} aria-label="Coverage type">{COVERAGE_TYPES.map((t) => <option key={t} value={t}>{t.toLowerCase()}</option>)}</select></label>
          <div className="btns">
            <button type="submit" className="um-open um-primary" disabled={busy || !provider}>{busy ? 'Recording…' : 'Confirm: Assign coverage'}</button>
            <button type="button" className="um-open" onClick={() => setAct(null)} disabled={busy}>Back</button>
          </div>
        </form>
      )}
      {err && <p className="um-notice stop" role="alert">{err}</p>}
    </>
  );
}

/** A dated fact and its source, on two lines. */
function Fact({ fact, none }: { fact: VisitFact | null; none: string }) {
  if (!fact) return <span className="um-quiet">{none}</span>;
  return (
    <>
      {shortDate(fact.on)}{fact.state === 'TENTATIVE' ? ' (tentative)' : ''}
      <span className={`sc-src${fact.source === 'EMERGE' ? ' emerge' : ''}`}>{fact.source === 'EMERGE' ? 'eMerge' : 'board'} · {fact.what}</span>
    </>
  );
}

function ProviderSelect({ providers, value, onChange, label }: { providers: ProviderOption[]; value: string; onChange: (v: string) => void; label: string }) {
  return (
    <label>
      <span>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}>
        <option value="">Not yet decided</option>
        {providers.map((p) => <option key={p.id} value={p.id}>{p.name || 'Unnamed'}{p.service_region ? ` · ${p.service_region}` : ''}{p.buildings_covered ? ` · covers ${p.buildings_covered}` : ''}</option>)}
      </select>
    </label>
  );
}

type Command = 'schedule' | PlanCommand | 'blocker' | 'clear';
const COMMAND_LABEL: Record<Command, string> = {
  schedule: 'Schedule a visit', reschedule: 'Reschedule', provider: 'Assign provider', confirm: 'Confirm', complete: 'Complete visit', cancel: 'Cancel visit', blocker: 'Record blocker', clear: 'Clear blocker',
};

/**
 * One building, opened: the plan and its commands, the blocker, the
 * history, the contacts. Every command is a small form that names what it
 * needs and carries the plan's version.
 *
 * The body carries no table cell of its own (Tide phase 2), so it can sit in
 * this table's row (FacilityPanel below) and in the Scheduling board's
 * DetailRow. `row` is the table's row when there is one; the board opens it
 * with null and the panel reads everything from the building's detail, saying
 * "Loading" until it has it rather than offering a command it cannot judge.
 */
export function FacilityPanelBody({ facilityId, name, row, canPlan, providers, onChanged }: { facilityId: string; name: string; row: FacilityRow | null; canPlan: boolean; providers: ProviderOption[]; onChanged: (msg: string) => Promise<void> }) {
  const [detail, setDetail] = useState<FacilityDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [cmd, setCmd] = useState<Command | null>(null);
  const [date, setDate] = useState('');
  const [provider, setProvider] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const r = await schedulingApi.facility(facilityId);
      if (r.success && r.data) setDetail(r.data); else { setDetail(null); setErr(outcomeOf(r)); }
    } catch { setDetail(null); setErr('Could not reach the server.'); }
  }, [facilityId]);
  useEffect(() => { load(); }, [load]);

  const base: FacilityRow | null = detail || row;
  const plan: NextPlan | null = base ? base.next_plan : null;
  const blocker = base ? base.blocker : null;
  const open = (c: Command) => { setCmd(c); setFormErr(null); setDate(''); setText(''); setProvider(c === 'provider' && plan ? (plan.provider_employee_id || '') : ''); };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); if (!cmd) return;
    setBusy(true); setFormErr(null);
    try {
      let r;
      if (cmd === 'schedule') r = await schedulingApi.schedule(facilityId, { planned_on: date, provider_employee_id: provider || null, purpose: text.trim() || undefined, idempotency_key: freshKey() });
      else if (cmd === 'blocker') r = await schedulingApi.recordBlocker(facilityId, text.trim());
      else if (cmd === 'clear') r = await schedulingApi.clearBlocker(blocker!.id, text.trim() || undefined);
      else if (!plan) { setFormErr('There is no open visit plan to change.'); return; }
      else if (cmd === 'reschedule') r = await schedulingApi.planAction(plan.id, 'reschedule', { planned_on: date, reason: text.trim() || undefined, version: plan.version });
      else if (cmd === 'provider') r = await schedulingApi.planAction(plan.id, 'provider', { provider_employee_id: provider, version: plan.version });
      else if (cmd === 'confirm') r = await schedulingApi.planAction(plan.id, 'confirm', { note: text.trim() || undefined, version: plan.version });
      else if (cmd === 'complete') r = await schedulingApi.planAction(plan.id, 'complete', { completed_on: date || undefined, notes: text.trim() || undefined, version: plan.version });
      else r = await schedulingApi.planAction(plan.id, 'cancel', { reason: text.trim(), version: plan.version });
      if (!r.success) { setFormErr(outcomeOf(r, { wrote: true })); return; }
      setCmd(null);
      await onChanged(`${name}: ${COMMAND_LABEL[cmd].toLowerCase()} recorded.`);
      await load();
    } catch { setFormErr('Could not reach the server. Nothing was recorded.'); } finally { setBusy(false); }
  };

  const needsDate = cmd === 'schedule' || cmd === 'reschedule' || cmd === 'complete';
  const needsText = cmd === 'blocker' || cmd === 'cancel';
  const textLabel = cmd === 'schedule' ? 'Purpose (optional)' : cmd === 'reschedule' ? 'Why (optional)' : cmd === 'confirm' ? 'Note (optional)' : cmd === 'complete' ? 'Notes (optional)' : cmd === 'cancel' ? 'Why (required)' : cmd === 'blocker' ? 'Why this building cannot be scheduled' : 'Note (optional)';
  const disabled = busy || (needsDate && cmd !== 'complete' && !date) || (needsText && !text.trim()) || (cmd === 'provider' && !provider);

  if (!base) {
    return (
      <div className="sc-panel" role="group" aria-label={`${name} operations`}>
        {err ? <p className="um-notice stop" role="alert">{err} <button type="button" className="um-open" onClick={load}>Try again</button></p> : <p className="um-quiet" role="status">Loading this building…</p>}
      </div>
    );
  }
  return (
      <div className="sc-panel" role="group" aria-label={`${name} operations`}>
        <div>
          <h3>Visit plan</h3>
          {plan ? (
            <p>
              <strong>{shortDate(plan.planned_on)}</strong> · {PLAN_STATE_LABEL[plan.state]} · {plan.provider_name || 'no provider yet'}{plan.purpose ? ` · ${plan.purpose}` : ''}
              {plan.expedited_request_id ? <span className="sc-tag attn" style={{ marginLeft: 6 }}>expedited</span> : null}
            </p>
          ) : <p className="um-quiet">No visit planned in eMerge. {base.next_visit ? `The board shows ${shortDate(base.next_visit.on)}${base.next_visit.state === 'TENTATIVE' ? ' (tentative)' : ''}.` : ''}</p>}
          {canPlan && (
            <div className="sc-cmds">
              {!plan && <button type="button" className="um-open" aria-pressed={cmd === 'schedule'} onClick={() => open('schedule')}>{COMMAND_LABEL.schedule}</button>}
              {plan && (['reschedule', 'provider', ...(plan.state === 'PLANNED' ? ['confirm'] : []), 'complete', 'cancel'] as Command[]).map((c) => (
                <button key={c} type="button" className="um-open" aria-pressed={cmd === c} onClick={() => open(c)}>{COMMAND_LABEL[c]}</button>
              ))}
              {blocker
                ? <button type="button" className="um-open" aria-pressed={cmd === 'clear'} onClick={() => open('clear')}>{COMMAND_LABEL.clear}</button>
                : <button type="button" className="um-open" aria-pressed={cmd === 'blocker'} onClick={() => open('blocker')}>{COMMAND_LABEL.blocker}</button>}
            </div>
          )}
          {canPlan && cmd && (
            <form className="um-form" onSubmit={submit} aria-label={`${COMMAND_LABEL[cmd]} for ${name}`}>
              {needsDate && <label><span>{cmd === 'complete' ? 'Visit date (today if blank)' : 'Visit date'}</span><input type="date" value={date} onChange={(e) => setDate(e.target.value)} aria-label="Visit date" /></label>}
              {(cmd === 'schedule' || cmd === 'provider') && <ProviderSelect providers={providers} value={provider} onChange={setProvider} label="Provider" />}
              <label><span>{textLabel}</span><input value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} aria-label={textLabel} /></label>
              {cmd === 'complete' && <p className="um-quiet">This records that the visit happened as planned. It is Scheduling's record, not the clinical visit, and it starts nothing downstream.</p>}
              <div className="btns">
                <button type="submit" className="um-open um-primary" disabled={disabled}>{busy ? 'Recording…' : `Confirm: ${COMMAND_LABEL[cmd]}`}</button>
                <button type="button" className="um-open" onClick={() => setCmd(null)} disabled={busy}>Back</button>
              </div>
              {formErr && <p className="um-notice stop" role="alert">{formErr}</p>}
            </form>
          )}
          {!canPlan && <p className="um-quiet">Read-only: the visits write is not open for this account.</p>}
        </div>
        <div>
          <h3>Blocker</h3>
          {blocker ? <p><strong>{blocker.reason}</strong><span className="sc-src">recorded {shortDate(blocker.recorded_at)}{blocker.recorded_by_name ? ` by ${blocker.recorded_by_name}` : ''}</span></p> : <p className="um-quiet">None open.</p>}
          {base.credentialing_status && <p><FilledStatusChip word={base.credentialing_status} column={SNF_CREDENTIALING} size="sm" /> <span className="sc-src">board credentialing word — shown, not enforced</span></p>}
          <CoverageSection facilityId={facilityId} facilityName={name} providers={base.providers} options={providers} canPlan={canPlan} onChanged={async (m) => { await onChanged(m); await load(); }} />
          {detail && detail.contacts.length > 0 && (
            <>
              <h3>Contacts</h3>
              <ul>{detail.contacts.map((c) => <li key={c.id}>{c.name}{c.title ? ` · ${c.title}` : ''}{c.phone ? ` · ${c.phone}` : ''}</li>)}</ul>
            </>
          )}
        </div>
        <div>
          <h3>History</h3>
          {err && <p className="um-notice stop" role="alert">{err}</p>}
          {!detail && !err && <p className="um-quiet">Loading…</p>}
          {detail && detail.plans.length === 0 && <p className="um-quiet">No visit plans recorded in eMerge yet.</p>}
          {detail && detail.plans.length > 0 && (
            <ul aria-label="Visit plans">
              {detail.plans.map((p) => <li key={p.id}>{shortDate(p.planned_on)} · {PLAN_STATE_LABEL[p.state]}{p.provider_name ? ` · ${p.provider_name}` : ''}{p.state === 'COMPLETED' && p.completed_on ? ` · done ${shortDate(p.completed_on)}` : ''}{p.state === 'CANCELLED' && p.cancelled_reason ? ` · ${p.cancelled_reason}` : ''}</li>)}
            </ul>
          )}
          {detail && detail.blockers.some((b) => b.cleared_at) && (
            <ul aria-label="Cleared blockers">
              {detail.blockers.filter((b) => b.cleared_at).map((b) => <li key={b.id} className="um-quiet">{b.reason} · cleared {shortDate(b.cleared_at!)}{b.cleared_by_name ? ` by ${b.cleared_by_name}` : ''}</li>)}
            </ul>
          )}
          <dl className="sc-facts">
            <dt>Last visit, every fact</dt>
            <dd>{base.last_visit_facts.length === 0 ? 'nothing recorded' : base.last_visit_facts.map((f) => `${shortDate(f.on)} (${f.source === 'EMERGE' ? 'eMerge' : 'board'}: ${f.what})`).join(' · ')}</dd>
            <dt>Board</dt>
            <dd>{base.scheduling_state.board || 'no scheduling row'}{base.scheduling_state.board_updated_at ? ` · mirrored ${shortDate(base.scheduling_state.board_updated_at)}` : ''}</dd>
          </dl>
        </div>
      </div>
  );
}

/** This table's row panel: the body in a cell spanning the table's nine columns. */
function FacilityPanel({ row, canPlan, providers, onChanged }: { row: FacilityRow; canPlan: boolean; providers: ProviderOption[]; onChanged: (msg: string) => Promise<void> }) {
  return (
    <td colSpan={9} className="sc-panel-cell">
      <FacilityPanelBody facilityId={row.id} name={row.name} row={row} canPlan={canPlan} providers={providers} onChanged={onChanged} />
    </td>
  );
}

/** The manifest link eMerge holds; Monday's link only as a labelled reference (MAN-4). */
function ManifestCell({ manifest }: { manifest: FacilityRow['manifest'] }) {
  if (manifest.state === 'EMERGE' && manifest.url) {
    return (
      <>
        <a href={manifest.url} target="_blank" rel="noreferrer noopener">Cloud manifest ↗</a>
        <span className="sc-src emerge">{manifest.origin === 'MONDAY_IMPORT' ? 'eMerge · copied once from Monday' : 'eMerge'}</span>
      </>
    );
  }
  if (manifest.state === 'CLEARED') return <span className="um-quiet">no link (cleared in eMerge)</span>;
  if (manifest.monday_reference) {
    return (
      <>
        <span className="um-quiet">not set in eMerge</span>
        <span className="sc-src"><a href={manifest.monday_reference.url} target="_blank" rel="noreferrer noopener">Monday reference ↗</a> · reference only</span>
      </>
    );
  }
  return <span className="um-quiet">not set in eMerge</span>;
}

function Row({ row, open, onOpen, canPlan, providers, onChanged }: { row: FacilityRow; open: boolean; onOpen: () => void; canPlan: boolean; providers: ProviderOption[]; onChanged: (msg: string) => Promise<void> }) {
  const covering = row.providers.filter((p) => p.status === 'ACTIVE');
  const proposed = row.providers.filter((p) => p.status !== 'ACTIVE');
  return (
    <>
      <tr className={`${row.blocker ? 'blocked' : ''}${open ? ' open-row' : ''}`}>
        <td><strong>{row.name}</strong><span className="sc-src">{[row.city, row.state].filter(Boolean).join(', ')}{row.region ? ` · ${row.region}` : ''}</span></td>
        <td className="num"><Fact fact={row.last_visit} none="—" /></td>
        <td className="num"><Fact fact={row.next_visit} none="not scheduled" /></td>
        <td>
          {covering.length > 0 ? covering.map((p) => p.name || 'Unnamed').join(', ') : proposed.length > 0 ? <span className="um-quiet">{proposed.map((p) => p.name || 'Unnamed').join(', ')} <span className="sc-src">proposed, not decided</span></span> : <span className="um-quiet">none</span>}
          {row.next_plan && row.next_plan.provider_name && <span className="sc-src emerge">visit: {row.next_plan.provider_name}</span>}
        </td>
        <td><ManifestCell manifest={row.manifest} /></td>
        <td>
          {row.scheduling_state.native ? <span className="sc-tag ok">{PLAN_STATE_LABEL[row.scheduling_state.native]} in eMerge</span> : null}
          {row.scheduling_state.board ? <span style={{ marginLeft: row.scheduling_state.native ? 4 : 0 }}><FilledStatusChip word={row.scheduling_state.board} column={SCHEDULING_STATUS} size="sm" suffix={<span className="sc-src" style={{ display: 'inline' }}> board</span>} /></span> : null}
          {!row.scheduling_state.native && !row.scheduling_state.board && <span className="um-quiet">—</span>}
          {row.expedited_open > 0 && <span className="sc-tag attn" style={{ marginLeft: 4 }}>{row.expedited_open} expedited</span>}
        </td>
        <td>{row.credentialing_status ? <FilledStatusChip word={row.credentialing_status} column={SNF_CREDENTIALING} size="sm" /> : <span className="um-quiet">—</span>}</td>
        <td>{row.blocker ? <span className="sc-tag attn" title={row.blocker.reason}>{row.blocker.reason.length > 40 ? `${row.blocker.reason.slice(0, 40)}…` : row.blocker.reason}</span> : <span className="um-quiet">—</span>}</td>
        <td><button type="button" className="um-open" aria-expanded={open} onClick={onOpen} aria-label={`${open ? 'Close' : 'Open'} ${row.name}`}>{open ? 'Close' : 'Open'}</button></td>
      </tr>
      {open && <tr><FacilityPanel row={row} canPlan={canPlan} providers={providers} onChanged={onChanged} /></tr>}
    </>
  );
}

export default function FacilityOperations({ scope, onNotice }: { scope: SchedulingScope; onNotice: (msg: string) => void }) {
  const [lens, setLens] = useState<FacilityLens>('board');
  const [q, setQ] = useState('');
  const [typed, setTyped] = useState('');
  const [page, setPage] = useState(1);
  const [table, setTable] = useState<FacilityTable | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [providers, setProviders] = useState<ProviderOption[]>([]);
  const canPlan = Boolean(scope.can.plan);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const r = await schedulingApi.facilities({ q: q || null, lens, page });
      if (r.success && r.data) setTable(r.data); else { setTable(null); setErr(outcomeOf(r)); }
    } catch { setTable(null); setErr('Could not reach the server.'); }
  }, [q, lens, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!canPlan) return;
    (async () => { try { const r = await schedulingApi.providers(); if (r.success && r.data) setProviders(r.data); } catch { /* the picker is empty; the server still refuses a bad id */ } })();
  }, [canPlan]);

  const lenses = useMemo(() => FACILITY_LENSES, []);
  const onChanged = async (msg: string) => { onNotice(msg); await load(); };

  return (
    <section className="um-card" aria-label="Facility operations">
      <div className="sc-controls">
        <div className="sc-chips" role="group" aria-label="Which buildings">
          {lenses.map((l) => (
            <button key={l} type="button" className="um-open" aria-pressed={lens === l} title={scope.lenses ? scope.lenses[l] : undefined} onClick={() => { setLens(l); setPage(1); setOpenId(null); }}>
              {FACILITY_LENS_LABEL[l]}<span className="n">{table ? table.lenses[l] ?? '' : ''}</span>
            </button>
          ))}
        </div>
        <form className="sc-search" onSubmit={(e) => { e.preventDefault(); setQ(typed.trim()); setPage(1); }} role="search">
          <input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Building name" aria-label="Search buildings" />
          <button type="submit" className="um-open">Search</button>
          {q && <button type="button" className="um-open" onClick={() => { setTyped(''); setQ(''); setPage(1); }}>Clear</button>}
        </form>
      </div>
      {err && <p className="um-notice stop" role="alert" style={{ margin: 12 }}>{err} <button type="button" className="um-open" onClick={load}>Try again</button></p>}
      {!err && !table && <p className="sc-empty">Loading…</p>}
      {table && table.items.length === 0 && <p className="sc-empty">{q ? `No building matches "${q}" in this view.` : 'Nothing in this view.'}</p>}
      {table && table.items.length > 0 && (
        <div className="sc-scroll" role="region" aria-label="Facility operations table" tabIndex={0}>
          <table className="sc-table">
            <thead>
              <tr>
                <th>Facility</th><th>Last visit</th><th>Next visit</th><th>Assigned provider</th><th>Manifest</th><th>Scheduling state</th><th>Credentialing</th><th>Blocker</th><th><span className="um-quiet">Open</span></th>
              </tr>
            </thead>
            <tbody>
              {table.items.map((row) => <Row key={row.id} row={row} open={openId === row.id} onOpen={() => setOpenId(openId === row.id ? null : row.id)} canPlan={canPlan} providers={providers} onChanged={onChanged} />)}
            </tbody>
          </table>
        </div>
      )}
      {table && table.total > table.limit && (
        <div className="sc-pager">
          <span>{(table.page - 1) * table.limit + 1}–{Math.min(table.page * table.limit, table.total)} of {table.total}</span>
          <div className="btns">
            <button type="button" className="um-open" disabled={table.page <= 1} onClick={() => setPage(table.page - 1)}>Previous</button>
            <button type="button" className="um-open" disabled={table.page * table.limit >= table.total} onClick={() => setPage(table.page + 1)}>Next</button>
          </div>
        </div>
      )}
      <div className="sc-foot">
        <span><strong>Where each column comes from.</strong> "board" is Monday's scheduling mirror, refreshed twice a day; "eMerge" is what Scheduling recorded here. A visit planned here is shown ahead of the board's date.</span>
        <span>Credentialing is the board's word, shown prominently and enforcing nothing (CRED-RULING-001). A blocker is Scheduling's own note on why a building is on hold.</span>
        <span>"Complete visit" is Scheduling's record that the visit happened as planned. The clinical visit and what it generates stay with the provider's workflow.</span>
      </div>
    </section>
  );
}
